import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, User, CalendarDays,
  FileSpreadsheet, FileText as FilePdf, AlertTriangle
} from 'lucide-react';
import { useLocalStorage } from 'react-use';

// Utils & Config
import { STATUS_CFG, TABS } from '../../helpers/Project-Report/utils/statusConfig';
import { fmtDate, statusMapToApp, statusMapToApi } from '../../helpers/Project-Report/utils/helpers';
import { allCategories } from '../../helpers/Project-Report/data/allCategories';
import { getQueryParam } from '../../helpers/Project-Report/utils/params';
import { exportToExcel } from '../../helpers/Project-Report/utils/excel';

// Services
import { fetchProjectDetail, updateProjectTaskList, fetchPIC, fetchPICByKeyword, getUserByToken } from '../../services/projectReportServices';

// Components
import ListTab from './components/ListTab';
import GanttTab from './components/GanttTab';
import BoardTab from './components/BoardTab';
import MediaTab from './components/MediaTab';
import ProjectComments from './components/ProjectComments';
import { TeamMembers } from './components/TeamMembers';
import DocumentUpload from './components/DocumentUpload';

// Redux
import { useSelector } from 'react-redux';
import { useCurrentUserAccess } from '../../helpers/currentUserAccess';

const ProjectDetail = () => {
  const navigate = useNavigate();
  const [token] = useLocalStorage('token', '');
  const [searchParams] = useSearchParams();
  const isEdited = getQueryParam(searchParams, 'isEdited');
  const onIframe = getQueryParam(searchParams, 'onIframe');
  const [nik, setNik] = useState(searchParams.get('nik') || null);
  const location = useLocation();
  const { _, projectId } = useParams();
  const { name, permission } = useSelector((state) => state.user);
  const { isCheckingAuth } = useCurrentUserAccess(searchParams.get('nik'));

  // Data passed via navigate state
  const project = location.state?.project || {};
  const category = location.state?.category || {};

  const accentColor = category.color || '#14b8a6';
  const accentGradient = category.gradient || 'linear-gradient(135deg,#14b8a6,#0d9488)';

  const [activeTab, setActiveTab] = useState('list');
  const [showOverdueTooltip, setShowOverdueTooltip] = useState(false);
  // Lifted task state — shared across List tab (add/delete) dan Gantt tabs
  const [tasks, setTasks] = useState([]);
  const [hasUploadedFiles, setHasUploadedFiles] = useState(false);

  // --- PIC State (lifted from ListTab) ---
  const [picOptions, setPicOptions] = useState([]);
  const [picSearch, setPicSearch] = useState('');

  // --- Real API Integration State ---
  const [dbProject, setDbProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const initialTasksStr = useRef('[]');
  const debounceTimer = useRef(null);
  const topRef = useRef(null);

  // Scroll to top on mount
  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Timeout fallback for layout paints
    setTimeout(() => {
      window.scrollTo(0, 0);
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  const fetchCurrentUser = async () => {
    const currentUser = await getUserByToken(token);
    if (currentUser && currentUser.data && currentUser.data.nik) {
      setNik(currentUser.data.nik);
    }
  }

  useEffect(() => {
    if (nik == null) {
      fetchCurrentUser();
    }
  }, []);

  // --- PIC Fetch Functions (lifted from ListTab) ---
  const fetchpic = useCallback(async () => {
    try {
      const response = await fetchPIC();
      const formattedPic = response.map((p, idx) => ({
        ...p,
        id: parseInt(p.NIK, 10) || idx,
        name: p.nama,
        department: p.departemen || '',
        avatar: p.nama ? p.nama.substring(0, 2).toUpperCase() : 'U'
      }));
      setPicOptions(formattedPic);
    } catch (error) {
      console.error('Error fetching PIC:', error);
    }
  }, []);

  const handlePicSearch = useCallback(async (keyword) => {
    try {
      if (!keyword.trim()) {
        fetchpic();
        return;
      }
      const response = await fetchPICByKeyword(keyword);
      const formattedPic = response.map((p, idx) => ({
        ...p,
        id: parseInt(p.NIK, 10) || idx,
        name: p.nama,
        department: p.departemen || '',
        avatar: p.nama ? p.nama.substring(0, 2).toUpperCase() : 'U'
      }));
      setPicOptions(formattedPic);
    } catch (error) {
      console.error('Error searching PIC:', error);
    }
  }, [fetchpic]);

  // Fetch PIC on mount
  useEffect(() => {
    fetchpic();
  }, [fetchpic]);

  // Fetch Project Data on Mount
  useEffect(() => {
    setIsLoading(true);
    fetchProjectDetail(projectId)
      .then(p => {
        if (p) {
          setDbProject(p);
          // Parse tasks from taskList JSON string. Fallback to [] if empty
          let parsedTasks = [];
          try {
            if (p.taskList) {
              // If it's already an object/array, use it directly. Otherwise parse.
              const rawTasks = typeof p.taskList === 'string' ? JSON.parse(p.taskList) : p.taskList;
              if (Array.isArray(rawTasks)) {
                // Map the backend structure to our ListTab structure
                parsedTasks = rawTasks.map((t, idx) => {
                  const parseTimeline = (tl) => {
                    if (!tl) return { start: '', end: '' };
                    const parts = tl.split(' - ');
                    const formatYMD = (dStr) => {
                      if (!dStr) return '';
                      const p = dStr.split('/');
                      if (p.length === 3) return `${p[2]}-${p[0].padStart(2, '0')}-${p[1].padStart(2, '0')}`;
                      return dStr;
                    }
                    return {
                      start: formatYMD(parts[0]),
                      end: formatYMD(parts[1])
                    };
                  };

                  const sched = parseTimeline(t.scheduleTimeline || t.timeline);
                  const act = parseTimeline(t.actualTimeline);
                  return {
                    id: t.id || (idx + 1),
                    name: t.taskname || t.name || '',
                    description: t.description,
                    picExternal: t.picExternal || null,
                    pic: t.pic || '',
                    status: statusMapToApp[t.status] || 'on_planning',
                    scheduleStart: sched.start,
                    scheduleEnd: sched.end,
                    actualStart: act.start,
                    actualEnd: act.end,
                    progress: parseInt(t.progress, 10) || 0,
                    lastUpdate: t.lastUpdate || '',
                  };
                });
              }
            }
          } catch (e) {
            console.error("Failed to parse taskList", e);
          }
          if (!Array.isArray(parsedTasks)) parsedTasks = [];
          // Save the initial snapshot so we don't trigger auto-save on mount
          initialTasksStr.current = JSON.stringify(parsedTasks);
          setTasks(parsedTasks);
        }
      })
      .catch(err => console.error("Failed to fetch project detail:", err))
      .finally(() => setIsLoading(false));
  }, [projectId]);

  // Auto-Save tasks to DB when `tasks` state changes
  useEffect(() => {
    // Prevent saving on initial load or if there's no actual change
    if (isLoading) return;
    if (JSON.stringify(tasks) === initialTasksStr.current) return;

    setSaveStatus('saving');

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      // Format data back to the API payload shape
      const formattedTasks = tasks.map(t => {
        const revertTimeline = (start, end) => {
          if (!start && !end) return '';
          // Revert YYYY-MM-DD to MM/DD/YYYY
          const fmtStr = (d) => {
            if (!d) return '';
            const p = d.split('-');
            if (p.length === 3) return `${p[1]}/${p[2]}/${p[0]}`;
            return d;
          };
          return `${fmtStr(start)} - ${fmtStr(end)}`;
        };

        return {
          taskname: t.name,
          description: t.description,
          picExternal: t.picExternal || null,
          pic: t.pic,
          date: t.scheduleStart ? revertTimeline(t.scheduleStart, '').split(' - ')[0] : '', // Adjust logic as needed by API
          status: statusMapToApi[t.status] || "1",
          timeline: revertTimeline(t.scheduleStart, t.scheduleEnd),
          scheduleTimeline: revertTimeline(t.scheduleStart, t.scheduleEnd),
          actualTimeline: revertTimeline(t.actualStart, t.actualEnd),
          progress: String(t.progress || 0),
          progressDate: Number(t.progress || 0),
          lastUpdate: t.lastUpdate || '',
        };
      });

      updateProjectTaskList(projectId, formattedTasks)
        .then(() => {
          setSaveStatus('saved');
          // Update ref to prevent successive duplicate saves
          initialTasksStr.current = JSON.stringify(tasks);
          setTimeout(() => setSaveStatus('idle'), 2000); // clear "saved" message after 2s
        })
        .catch(err => {
          console.error("Failed to auto-save tasks:", err);
          setSaveStatus('error');
        });
    }, 1000); // 1 second debounce

    return () => clearTimeout(debounceTimer.current);
  }, [tasks, projectId, isLoading]);

  // Computed display values
  const displayProject = dbProject || project;
  const displayTitle = displayProject.projectName || displayProject.title || 'Loading...';
  // Use dates from dbProject if available, else from passed state/dummy
  const displayDueDate = dbProject ? dbProject.dateEnd : '';

  // Compute true completion progress based on our local tasks array
  const totalTasks = tasks.length;
  const doneTasks = tasks.filter(t => (t?.status || '').toLowerCase().includes('done') || (t?.status || '').toLowerCase().includes('selesai')).length;
  const computedProgress = totalTasks > 0 ? (doneTasks == totalTasks ? 'done' : 'on_progress') : 'on_progress';

  // Update status based on progress 
  let computedStatusCfg = STATUS_CFG.on_progress;
  if (computedProgress === 'done') computedStatusCfg = STATUS_CFG.done;
  else if (computedProgress === 'on_progress') computedStatusCfg = STATUS_CFG.on_progress;

  // Helper untuk mengecek apakah project overdue
  const checkIsOverdue = () => {
    if (!displayDueDate || computedProgress === 'done') return false;
    
    let dueDateStr = displayDueDate;
    if (dueDateStr.includes('-')) {
      const parts = dueDateStr.split('-');
      if (parts[0].length === 2) {
        dueDateStr = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    } else if (dueDateStr.includes('/')) {
      const parts = dueDateStr.split('/');
      if (parts[0].length === 2 && parseInt(parts[0]) > 12) {
        dueDateStr = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    const dueDate = new Date(dueDateStr);
    const today = new Date();
    
    if (isNaN(dueDate.getTime())) return false;

    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    
    return today > dueDate;
  };
  const isOverdue = checkIsOverdue();

  if (isCheckingAuth) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100vw', background: '#f8fafc' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2.5rem', color: '#f97316' }}></i>
      </div>
    );
  }

  return (
    <div
      ref={topRef}
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: '28px 32px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* ── Breadcrumb ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 10px', borderRadius: '8px', color: '#64748b', fontSize: '13px', fontWeight: '500', transition: 'all 0.2s' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
        >
          <ArrowLeft size={14} />
          Kembali
        </button>
        <span style={{ color: '#cbd5e1' }}>/</span>
        <span style={{ fontSize: '13px', color: '#94a3b8' }}>{category.name || 'Proyek'}</span>
        <span style={{ color: '#cbd5e1' }}>/</span>
        <span style={{ fontSize: '13px', color: accentColor, fontWeight: '600' }}>Detail</span>
      </div>

      {/* ── Project Header Card ── */}
      <div
        style={{
          background: '#fff',
          borderRadius: '20px',
          padding: '28px 32px',
          marginBottom: '20px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: `1.5px solid ${accentColor}20`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Accent stripe */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: accentGradient, borderRadius: '20px 20px 0 0' }} />

        {/* Title */}
        <h1
          style={{
            margin: '0 0 20px',
            fontSize: '20px',
            fontWeight: '800',
            color: accentColor,
            lineHeight: 1.35,
            letterSpacing: '-0.3px',
          }}
        >
          {displayTitle}
        </h1>

        {/* Meta row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '32px', flexWrap: 'wrap' }}>
          {/* Project Manager */}
          <div>
            <div style={{ fontSize: '11px', color: accentColor, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <User size={11} /> Project Manager
            </div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>
              {displayProject.pic ? String(displayProject.pic).split('-')[1] || displayProject.pic : ''}
            </div>
          </div>

          <div style={{ width: '1px', height: '40px', background: '#e2e8f0' }} />

          {/* Status */}
          <div>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Status</div>
            <div
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '5px 14px', borderRadius: '20px',
                background: computedStatusCfg.bg, color: computedStatusCfg.color,
                fontSize: '13px', fontWeight: '700',
              }}
            >
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: computedStatusCfg.color, animation: computedProgress < 100 && computedProgress > 0 ? 'pulse 1.5s infinite' : 'none' }} />
              {computedStatusCfg.label}
            </div>
          </div>

          <div style={{ width: '1px', height: '40px', background: '#e2e8f0' }} />

          {/* Due Date */}
          <div>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CalendarDays size={11} /> Due Date
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{fmtDate(displayDueDate)}</div>
              {isOverdue && (
                <div 
                  style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                  onMouseEnter={() => setShowOverdueTooltip(true)}
                  onMouseLeave={() => setShowOverdueTooltip(false)}
                >
                  <div style={{
                     display: 'flex', alignItems: 'center', justifyContent: 'center',
                     background: '#fef2f2', color: '#ef4444',
                     padding: '4px', borderRadius: '6px', cursor: 'pointer',
                     boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)',
                     animation: 'pulse 2s infinite'
                  }}>
                    <AlertTriangle size={14} />
                  </div>

                  {/* Modern Tooltip Box */}
                  <div style={{
                    position: 'absolute',
                    bottom: '140%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#1e293b',
                    color: '#fff',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    width: 'max-content',
                    maxWidth: '220px',
                    textAlign: 'center',
                    lineHeight: '1.4',
                    opacity: showOverdueTooltip ? 1 : 0,
                    visibility: showOverdueTooltip ? 'visible' : 'hidden',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    zIndex: 10
                  }}>
                    Proyek ini telah melewati tenggat waktu (due date). Mohon perbarui jadwal agar tetap relevan.
                    
                    {/* Segitiga Pointer */}
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      borderWidth: '5px',
                      borderStyle: 'solid',
                      borderColor: '#1e293b transparent transparent transparent'
                    }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div style={{ width: '1px', height: '40px', background: '#e2e8f0' }} />

          {/* Team Members - derived from unique assigned PICs */}
          <TeamMembers tasks={tasks} />
        </div>
      </div>

      {/* ── Export Buttons ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
        {[
          { label: 'Export to Excel', icon: FileSpreadsheet, color: '#10b981', gradient: 'linear-gradient(135deg,#10b981,#059669)', onClick: () => exportToExcel(tasks, category, accentColor, displayProject, displayTitle, computedStatusCfg, displayDueDate) },
          { label: 'Export to PDF', icon: FilePdf, color: '#ef4444', gradient: 'linear-gradient(135deg,#ef4444,#dc2626)', onClick: undefined },
        ].map(({ label, icon: Icon, color, gradient, onClick }) => (
          <button
            key={label}
            onClick={onClick}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              padding: '14px 24px',
              borderRadius: '14px',
              border: 'none',
              background: gradient,
              color: '#fff',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: `0 4px 14px ${color}50`,
              letterSpacing: '0.4px',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 20px ${color}60`; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 4px 14px ${color}50`; }}
          >
            <Icon size={16} />
            {label.toUpperCase()}
          </button>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div
        style={{
          background: '#fff',
          borderRadius: '20px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: '1px solid #f1f5f9',
        }}
      >
        {/* Tab navigation */}
        <div
          style={{
            display: 'flex',
            borderBottom: '2px solid #f1f5f9',
            paddingTop: '4px',
            borderRadius: '20px 20px 0 0',
            overflow: 'hidden',
          }}
        >
          {TABS.map(({ id, label, icon: Icon }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '7px',
                  padding: '16px 20px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: '12.5px',
                  fontWeight: isActive ? '700' : '500',
                  color: isActive ? accentColor : '#64748b',
                  borderBottom: isActive ? `2.5px solid ${accentColor}` : '2.5px solid transparent',
                  marginBottom: '-2px',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = accentColor; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = '#64748b'; }}
              >
                <Icon size={14} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div style={{ padding: '24px' }}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', color: '#94a3b8' }}>Loading Data...</div>
          ) : (
            <>
              {/* Save status indicator */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', height: '16px', marginBottom: '8px', fontSize: '12px' }}>
                {saveStatus === 'saving' && <span style={{ color: '#f59e0b', fontWeight: '600' }}>Menyimpan...</span>}
                {saveStatus === 'saved' && <span style={{ color: '#10b981', fontWeight: '600' }}>Tersimpan ✓</span>}
                {saveStatus === 'error' && <span style={{ color: '#ef4444', fontWeight: '600' }}>Gagal menyimpan!</span>}
              </div>

              {activeTab === 'list' && <ListTab tasks={tasks} setTasks={setTasks} accent={accentColor} picOptions={picOptions} picSearch={picSearch} setPicSearch={setPicSearch} onPicSearch={handlePicSearch} onRefreshPic={fetchpic} permissions={permission} />}
              {activeTab === 'board' && <BoardTab tasks={tasks} setTasks={setTasks} accent={accentColor} picOptions={picOptions} picSearch={picSearch} setPicSearch={setPicSearch} onPicSearch={handlePicSearch} onRefreshPic={fetchpic} permissions={permission} />}
              {activeTab === 'actual' && <GanttTab tasks={tasks} mode="actual" accent={accentColor} dueDate={displayDueDate} />}
              {activeTab === 'schedule' && <GanttTab tasks={tasks} mode="schedule" accent={accentColor} dueDate={displayDueDate} />}
              {activeTab === 'media' && <MediaTab projectId={projectId} tasks={tasks} accent={accentColor} permissions={permission} />}
            </>
          )}
        </div>
      </div>

      {/* ── Document Upload and Comments Section ── */}
      {/* <div className={hasUploadedFiles ? "" : "lg:flex lg:gap-6 lg:items-start"}> */}
      <div className={"lg:flex lg:gap-6 lg:items-start"}>
        <div className={"flex-1 min-w-0"}>
          <DocumentUpload accentColor={accentColor} onFilesChange={setHasUploadedFiles} projectId={projectId} nik={nik} categoryFile={"report"} sectionName={"Dokumen Project"} permissions={permission} defaultAllowedDoc={true} />
        </div>
        <div className={"flex-1 min-w-0"}>
          <DocumentUpload accentColor={accentColor} onFilesChange={setHasUploadedFiles} projectId={projectId} nik={nik} categoryFile={"cost"} sectionName={"Dokumen Cost"} permissions={permission} defaultAllowedDoc={false} pimproNik={dbProject?.pic} />
        </div>
      </div>
      <ProjectComments projectId={projectId} accent={accentColor} nik={nik} />

      {/* ── Keyframes ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(1.3); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        @keyframes fadeInScale {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default ProjectDetail;
